import Typography from '@mui/material/Typography';
import { Iconify } from '@mfw/components';
import Button from '@mui/material/Button';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';

import type { OrgBillingLockReason } from '@mfw/common';

import type { Routes } from '../../router';

import { buildAccountBillingRoute } from '../AccountBilling';
import { usePermissionsContext, useAuthContext } from '../../context';
import { useNavigate } from '../../router/hooks';
import { orgLockCopy } from './orgLockCopy';
import messages from './messages';

/**
 * §16 (R-AUD-62): org section lock screen — renders INSTEAD OF org routes
 * while the platform billing restricts the organization. A holder of the
 * `billing_read` permission gets a CTA into the Account settings →
 * Subscriptions area (the finance route is excluded from the lock so the
 * situation can be resolved); other members get instructions to contact an
 * admin.
 *
 * T19 — the CTA used to land on the legacy `FinancePage`, which now just
 * redirects here anyway for a regular user (the only kind that ever reaches
 * this screen — `OrganizationGuard`'s bypass keeps a platform actor out of
 * the lock entirely, P3), so this screen needs no platform variant.
 *
 * Q3/Task 4 — TWO states lock an organization and they say OPPOSITE things
 * about the customer:
 *
 *  - `'unpaid'` — there is a debt, the way out is paying, and the CTA is a
 *    pay-now context (the `overview` tab, same as `BillingStatusBanner`'s);
 *  - `'subscription_ended'` — the plan was cancelled and the paid period ran
 *    out. NOTHING is owed. There is no balance to settle, so a pay-now context
 *    answers nothing: the CTA goes to the `subscriptions` tab, where the
 *    package catalog is, and neither title nor body mentions a balance.
 *
 * The reason arrives on the organization ROW (`billingLockReason`), not off
 * the billing overview — the people who see this screen are overwhelmingly
 * clients and trainers, who hold no `billing_read` and never load the overview
 * at all.
 */
export const OrgBillingLockedScreen = ({
  organizationId,
  reason,
}: {
  organizationId: string;
  reason?: OrgBillingLockReason | null;
}) => {
  const { formatMessage } = useIntl();
  const { hasPermission } = usePermissionsContext();
  const { currentUser } = useAuthContext();
  const navigate = useNavigate();
  const canSeeBilling = hasPermission('billing_read');
  // One decision, shared with the public organization profile — see
  // `orgLockCopy`. Two screens explaining the same lock must never drift.
  const { body, hasEnded, title } = orgLockCopy(reason);

  const goToBilling = () => {
    navigate(
      buildAccountBillingRoute({
        userId: currentUser?.id ?? '',
        scope: organizationId,
        tab: hasEnded ? 'subscriptions' : 'overview',
      }) as Routes,
    );
  };

  return (
    <Box
      data-testid="org-billing-locked-screen"
      justifyContent="center"
      flexDirection="column"
      alignItems="center"
      display="flex"
      height="100%"
      gap={2}
      p={4}
    >
      <Iconify icon="mdi:lock-outline" size="large" width={64} />
      <Typography variant="h5">{formatMessage(title)}</Typography>
      <Typography
        color="text.secondary"
        textAlign="center"
        variant="body1"
        maxWidth={480}
      >
        {formatMessage(body)}
        {!canSeeBilling && ` ${formatMessage(messages.orgLockedBodyMember)}`}
      </Typography>
      {canSeeBilling && (
        <Button
          data-testid={
            hasEnded ? 'org-locked-choose-plan' : 'org-locked-go-to-billing'
          }
          onClick={goToBilling}
          variant="contained"
        >
          {formatMessage(
            hasEnded
              ? messages.orgEndedChoosePlan
              : messages.orgLockedGoToBilling,
          )}
        </Button>
      )}
    </Box>
  );
};
